import type { InputPreset, InputType } from './_types';

function lastHangulCharCode(word: string): number | null {
  for (let i = word.length - 1; i >= 0; i--) {
    const code = word.charCodeAt(i);
    if (code >= 0xac00 && code <= 0xd7a3) return code;
  }
  return null;
}

export function eulReul(word: string) {
  const code = lastHangulCharCode(word);
  if (code === null) return '를';
  return (code - 0xac00) % 28 === 0 ? '를' : '을';
}

export function eunNeun(word: string) {
  const code = lastHangulCharCode(word);
  if (code === null) return '는';
  return (code - 0xac00) % 28 === 0 ? '는' : '은';
}

export type Validator = (value: string, label?: string) => string | null;

/**
 * SF(Salesforce) 필드 타입별 값 제약 상수.
 * - TEXT      : 최대 255자
 * - TEXTAREA  : 최대 100,000자
 * - NUMBER    : 최대 18자리 정수
 * - PICKLIST  : 단건 선택 (string)
 * - MULTIPICKLIST : 복수 선택 (string[])
 */
export const SF_FIELD_CONSTRAINTS = {
  TEXT:          { maxLength: 255 },
  TEXTAREA:      { maxLength: 100_000 },
  NUMBER:        { maxDigits: 18 },
  PICKLIST:      { multiple: false },
  MULTIPICKLIST: { multiple: true },
} as const;

/** 희망 면적(평) — Salesforce·업무 규칙상 상한. */
export const OFFICE_AREA_MAX_PYEONG = 100_000;
const OFFICE_AREA_MAX_DISPLAY = OFFICE_AREA_MAX_PYEONG.toLocaleString('en-US');

/**
 * InputType별 기본 유효성 검사 함수.
 * - 빈 값은 검사하지 않음 (required 체크는 별도)
 * - validate prop을 직접 전달하면 기본값을 덮어씁니다
 */
export const DEFAULT_VALIDATORS: Partial<Record<InputType, Validator>> = {
  text: (value, label = '텍스트') => {
    if (value.length > SF_FIELD_CONSTRAINTS.TEXT.maxLength)
      return `${label}${eulReul(label)} ${SF_FIELD_CONSTRAINTS.TEXT.maxLength}자 이하로 입력해 주세요.`;
    return null;
  },

  number: (value, label = '숫자') => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > SF_FIELD_CONSTRAINTS.NUMBER.maxDigits)
      return `${label}${eulReul(label)} ${SF_FIELD_CONSTRAINTS.NUMBER.maxDigits}자리 이하로 입력해 주세요.`;
    return null;
  },

  email: (value, label = '이메일 주소') => {
    if (!value.trim()) return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ? null
      : `올바른 ${label}${eulReul(label)} 입력해 주세요.`;
  },

  tel: (value, label = '전화번호') => {
    if (!value.trim()) return null;
    const digits = value.replace(/\D/g, '');
    // 010 휴대전화: 010 + 중간 4자리 + 끝 4자리 (총 11자리)
    if (digits.startsWith('010')) {
      return /^010\d{8}$/.test(digits)
        ? null
        : `올바른 ${label}${eulReul(label)} 입력해 주세요.`;
    }
    return digits.length >= 9 && digits.length <= 11
      ? null
      : `올바른 ${label}${eulReul(label)} 입력해 주세요.`;
  },

  date: (value, label = '날짜') => {
    if (!value.trim()) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return `올바른 ${label}${eulReul(label)} 입력해 주세요. (YYYY-MM-DD)`;
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
      ? null
      : '존재하지 않는 날짜입니다.';
  },

  yearMonth: (value, label = '날짜') => {
    if (!value.trim()) return null;
    if (!/^\d{4}-\d{2}$/.test(value)) return `올바른 ${label}${eulReul(label)} 입력해 주세요. (YYYY-MM)`;
    const [y, m] = value.split('-').map(Number);
    if (m < 1 || m > 12) return '올바른 월을 입력해 주세요.';
    const today = new Date();
    const inputDate = new Date(y, m - 1);
    const currentMonth = new Date(today.getFullYear(), today.getMonth());
    return inputDate >= currentMonth ? null : '현재 월 이후의 날짜를 입력해 주세요.';
  },
};

/**
 * InputPreset별 유효성 검사 함수.
 * - validate prop → VARIANT_VALIDATORS → DEFAULT_VALIDATORS 순으로 우선 적용
 */
export const VARIANT_VALIDATORS: Partial<Record<InputPreset, Validator>> = {
  phone: (value, label = '휴대전화 번호') => {
    if (!value.trim()) return null;
    const digits = value.replace(/\D/g, '');
    const invalidMessage = `올바른 ${label}${eulReul(label)} 입력해 주세요.`;
    if (!/^010\d{7,8}$/.test(digits)) return invalidMessage;
    // 뒷자리가 전부 0이면(000-0000, 0000-0000) 가짜/테스트 번호로 간주해 차단
    const body = digits.slice(3);
    if (/^0+$/.test(body)) return invalidMessage;
    return null;
  },

  userCount: (value, label = '인원') => {
    if (!value.trim()) return null;
    const n = Number(value.replace(/,/g, ''));
    return Number.isInteger(n) && n >= 1 && n <= 999
      ? null
      : `${label}${eulReul(label)} 1~999 사이로 입력해 주세요.`;
  },

  officeArea: (value, label = '희망 면적(평)') => {
    if (!value.trim()) return null;
    const n = Number(value.replace(/,/g, ''));
    return Number.isInteger(n) && n <= OFFICE_AREA_MAX_PYEONG
      ? null
      : `${label}${eulReul(label)} ${OFFICE_AREA_MAX_DISPLAY}평 이하로 입력해 주세요.`;
  },
};
